import {
    AuctionCardState,
    AuctionSummary,
    CarouselLot,
    fmtDateTime,
} from '@viewbid/ui';
import {
    AuctionLotGroupItem,
    GetFeaturedAuctions200ResponseAllOfDataInner,
} from '@viewbid/ts-angular-client';
import { wireRead } from './wire-read.util';

const CATEGORY_TONE: Record<string, string> = {
    clothing: '#33cc00',
    collectibles: '#006633',
    fitness_equipment: '#33ff99',
    furniture_and_appliances: '#00ccff',
    hardware: '#cc0000',
    jewelry: '#ad8533',
    kids_clothing: '#660099',
    office_equipment: '#993300',
    online_retailer: '#e2002d',
    restaurant_equipment: '#bfbfbf',
    third_party_seized_vehicle: '#000066',
    tools: '#ffcc00',
    vehicles_equip_ind_tools: '#d05300',
};

const DEFAULT_TONE: string = '#33cc00';
const NO_IMAGE_FALLBACK: string = '/assets/img/brand/no-image-lot.svg';
const HERO_PANEL_BG: string = '/assets/viewbid/images/auction-panel-bg.png';
// 0xA6 ≈ 0.65 alpha — matches the saturated `.bg-vb-category-X` stop from the
// legacy card. (The lighter 0.15 stop was the carousel-strip tint, not the card.)
const HERO_TINT_ALPHA: string = 'a6';

export function getCategoryTone(category?: string): string {
    if (!category) return DEFAULT_TONE;
    return CATEGORY_TONE[category.toLowerCase()] ?? DEFAULT_TONE;
}

export function buildHeroTintImage(category?: string): string {
    const tinted: string = `${getCategoryTone(category)}${HERO_TINT_ALPHA}`;
    return `linear-gradient(${tinted}, ${tinted}), url('${HERO_PANEL_BG}') center/cover no-repeat`;
}

export function getAuctionCardState(
    auction: GetFeaturedAuctions200ResponseAllOfDataInner,
    now: Date = new Date(),
): AuctionCardState {
    const start: Date = new Date(auction.fromDatetime);
    const end: Date = new Date(auction.toDatetime);

    if (now < start) return 'upcoming';
    if (now >= end) return 'closed';

    if (
        auction.firstLotCloseTime &&
        new Date(auction.firstLotCloseTime) <= now
    ) {
        return 'closing';
    }

    return 'open';
}

export function mapToAuctionSummary(
    auction: GetFeaturedAuctions200ResponseAllOfDataInner,
    now: Date = new Date(),
): AuctionSummary {
    const start: Date = new Date(auction.fromDatetime);
    const end: Date = new Date(auction.toDatetime);
    const firstLotClose: Date | null = auction.firstLotCloseTime
        ? new Date(auction.firstLotCloseTime)
        : null;

    const lotCount: number = auction.numLots ?? auction.lots?.length ?? 0;
    const closedLots: number = (auction.lots ?? []).filter(
        (lot: AuctionLotGroupItem) => new Date(lot.toDatetime) <= now,
    ).length;
    const lotsOpen: number = Math.max(lotCount - closedLots, 0);

    const startsClosingTarget: Date = firstLotClose ?? end;
    const startsClosingInSec: number = Math.max(
        Math.floor((startsClosingTarget.getTime() - now.getTime()) / 1000),
        0,
    );
    const startsInSec: number = Math.max(
        Math.floor((start.getTime() - now.getTime()) / 1000),
        0,
    );

    return {
        id: auction.id,
        title: auction.name,
        organizer: 'Viewbid',
        type: auction.auctionType ?? '',
        heroImage: buildHeroTintImage(auction.auctionType),
        lotCount,
        lotsOpen,
        closedLots,
        bp: auction.buyerPremium || 0,
        startsClosingInSec,
        startsInSec,
        previewsAt: fmtDateTime(start),
        startsClosingAt: fmtDateTime(startsClosingTarget),
        closedAt: fmtDateTime(end),
    };
}

/**
 * Auction-lot id under both wires: canonical rows say auctionLotsId, the
 * legacy wire said id. Sockets and lot-jump routes key on this id.
 */
export function carouselLotId(lot: AuctionLotGroupItem): string | undefined {
    return wireRead<string>(lot, 'auctionLotsId', 'id');
}

// Legacy wire sent images as a single thumbnail URL string; canonical rows
// carry thumbnailUrl plus an images asset array.
function carouselImageUrl(lot: AuctionLotGroupItem): string {
    const wired: unknown = wireRead(lot, 'thumbnailUrl', 'images');
    if (typeof wired === 'string' && wired) return wired;
    return lot.images?.[0]?.url ?? NO_IMAGE_FALLBACK;
}

export function mapLotsToCarouselLots(
    lots: AuctionLotGroupItem[],
): CarouselLot[] {
    return (lots ?? []).map((lot: AuctionLotGroupItem) => ({
        id: carouselLotId(lot) ?? '',
        lotNumber: `${lot.number ?? ''}${lot.suffix ?? ''}`,
        title: lot.title,
        tone: getCategoryTone(lot.category ?? undefined),
        imageUrl: carouselImageUrl(lot),
        currentBid: lot.currentBid,
        bidCount: 0,
    }));
}
